import { createBrowserClient } from '@supabase/ssr';

import { getTenantConfigFromSession } from '@/lib/tenant/session';

import { env } from '@/env';

import { Database } from '@/types/supabase';

/**
 * Create a Supabase browser client
 * Requires tenant config from session (stored during login)
 * All connections must go through the middleware database
 */
export function createSupabaseBrowserClient() {
  // During SSR, we can't access localStorage, so return a placeholder client
  // Browser-only code should check for tenant config before using
  if (typeof window === 'undefined') {
    // SSR: return a non-singleton placeholder — each SSR render is its own module
    // context so no singleton pollution risk, but isSingleton:false is safest.
    return createBrowserClient<Database>(
      'https://placeholder.supabase.co',
      'placeholder-key',
      { isSingleton: false },
    );
  }

  // Get tenant config from session (stored during login)
  const tenantConfig = getTenantConfigFromSession();

  if (
    !tenantConfig ||
    !tenantConfig.supabase_url ||
    !tenantConfig.supabase_anon_key
  ) {
    // No tenant config yet (e.g. login page before auth).
    // MUST use isSingleton:false — @supabase/ssr holds ONE global instance regardless
    // of URL. If we let this placeholder become the singleton, loginUser's
    // createBrowserClient(realUrl, realKey) will get the placeholder back and
    // route signInWithPassword to placeholder.supabase.co.
    return createBrowserClient<Database>(
      'https://placeholder.supabase.co',
      'placeholder-key',
      { isSingleton: false },
    );
  }

  // Use tenant-specific config
  return createBrowserClient<Database>(
    tenantConfig.supabase_url,
    tenantConfig.supabase_anon_key,
  );
}

/**
 * Create a Supabase browser client for the middleware (main) database.
 * Use this for bucket operations — all image buckets live here.
 */
export function createMiddlewareBrowserClient() {
  if (typeof window === 'undefined') {
    return createBrowserClient<Database>(
      'https://placeholder.supabase.co',
      'placeholder-key',
      { isSingleton: false },
    );
  }

  return createBrowserClient<Database>(
    env.NEXT_PUBLIC_SUPABASE_MIDDLEWARE_URL,
    env.NEXT_PUBLIC_SUPABASE_MIDDLEWARE_ANON_KEY,
    { isSingleton: false },
  );
}

// Lazy singleton for backward compatibility
// Use createSupabaseBrowserClient() directly in new code
let _supabaseInstance: ReturnType<typeof createSupabaseBrowserClient> | null =
  null;
let _isPlaceholderCached = false;

export function resetSupabaseBrowserClient() {
  _supabaseInstance = null;
  _isPlaceholderCached = false;
}

function getSupabaseInstance() {
  // During SSR, return a placeholder that will error if used
  if (typeof window === 'undefined') {
    // Create a minimal client that will fail gracefully if used during SSR
    return createBrowserClient<Database>(
      'https://placeholder.supabase.co',
      'placeholder-key',
    );
  }

  // If we have a cached placeholder, but now we have tenant config, clear the cached placeholder
  if (_isPlaceholderCached) {
    const tenantConfig = getTenantConfigFromSession();
    if (tenantConfig && tenantConfig.supabase_url && tenantConfig.supabase_anon_key) {
      resetSupabaseBrowserClient();
    }
  }

  if (!_supabaseInstance) {
    const tenantConfig = getTenantConfigFromSession();
    if (!tenantConfig || !tenantConfig.supabase_url || !tenantConfig.supabase_anon_key) {
      _isPlaceholderCached = true;
    } else {
      _isPlaceholderCached = false;
    }
    _supabaseInstance = createSupabaseBrowserClient();
  }
  return _supabaseInstance;
}

// Export as a Proxy to make it lazy and prevent initialization at module load
export const supabase = new Proxy(
  {} as ReturnType<typeof createSupabaseBrowserClient>,
  {
    get(_target, prop) {
      const instance = getSupabaseInstance();
      const value = (instance as any)[prop];
      if (typeof value === 'function') {
        return value.bind(instance);
      }
      return value;
    },
  },
);
